import { useState } from 'react';
import classNames from 'classnames';
import { Container } from 'components/gruenland/atoms/container/Container';
import Link from 'components/gruenland/atoms/link';
import Slider from 'components/gruenland/atoms/slider/Slider';
import Typography from 'components/gruenland/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocalizedString from 'helpers/hooks/useLocalizedString';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { TYPOGRAPHY_PDP_TECHNOLOGY_TITLE, TYPOGRAPHY_PRODUCT_DETAIL_TITLE } from 'lib/typography';
import TechnologyImage from './TechnologyImage';
import { TechnologyObject } from './types';

type Props = {
  technologies: TechnologyObject[];
};

const TechnologiesSlider: React.FC<Props> = ({ technologies }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const [currentTechnology, setCurrentTechnology] = useState(0);
  const { formatString } = useLocalizedString();
  const [isDesktopSize] = useMediaQuery(desktop);

  return (
    <Container>
      <h2 className={classNames(TYPOGRAPHY_PRODUCT_DETAIL_TITLE, 'text-center')}>
        {formatMessage({ id: 'product.technologies', defaultMessage: 'Tecnologie' })}
      </h2>
      <div className="mt-10 h-[600px] lg:grid lg:h-[700px] lg:grid-cols-2">
        <div className="relative hidden h-full overflow-auto lg:block">
          {technologies.map((t, idx) => (
            <TechnologyImage
              key={t.id}
              image={t.obj?.value?.images?.[0]}
              className={classNames('absolute size-full object-cover transition-opacity duration-300', {
                'opacity-0': currentTechnology !== idx,
                'opacity-100': currentTechnology === idx,
              })}
            />
          ))}
        </div>
        <Slider
          slidesPerView={1}
          slidesPerGroup={1}
          dots={false}
          arrows={isDesktopSize}
          arrowsPosition="inside"
          allowTouchMove
          className="size-full"
          onSlideChange={(swiper) => setCurrentTechnology(swiper.activeIndex)}
        >
          {technologies.map((t) => (
            <div key={t.id} className="h-full lg:px-[100px]">
              <div className="mb-5 h-full text-center">
                <div className="h-1/4 overflow-auto lg:hidden">
                  <TechnologyImage image={t.obj?.value?.images?.[0]} className="size-full object-cover" />
                </div>
                <div className="h-1/4 overflow-auto lg:h-1/2">
                  <TechnologyImage
                    image={t.obj?.value?.images?.[1] || t.obj?.value?.images?.[0]}
                    className="size-full object-scale-down"
                  />
                </div>
                <div className="flex h-1/2 flex-col justify-center gap-3 pt-5">
                  <Typography className={TYPOGRAPHY_PDP_TECHNOLOGY_TITLE}>{t.obj?.value?.name}</Typography>
                  <Typography>{t.obj?.value?.shortDescription}</Typography>
                  <Typography>{t.obj?.value?.longDescription}</Typography>
                  {t.obj?.value?.link && (
                    <Link link={formatString(t.obj.value.link)} className="underline">
                      {formatMessage({ id: 'product.technologyLink', defaultMessage: 'Scopri la tecnologia Grünland' })}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </Container>
  );
};

export default TechnologiesSlider;
